/**
 * Two-factor authentication, for the signed-in user.
 *
 * The QR code is rendered from the otpauth:// URI Firebase mints. The secret is
 * also shown as text, because the commonest case is someone enrolling ON the
 * phone that holds the authenticator - and a phone cannot scan its own screen.
 *
 * NOTHING IS ENROLLED until a valid code is submitted. Abandoning this card
 * half-way leaves the account exactly as it was, which is why the secret can be
 * generated freely on open.
 */

import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { httpsCallable } from 'firebase/functions';
import type { MultiFactorInfo, TotpSecret } from 'firebase/auth';
import { auth, functions } from '../firebase';
import { useOrg } from '../org';
import {
  beginEnrollment, finishEnrollment, unenroll, enrolledFactors,
  isVerified, sendVerification, mfaErrorText,
} from '../lib/mfa';

interface ProjectStatus {
  ok: boolean;
  ready: boolean;
  enabled?: boolean;
  message?: string;
  detail?: string;
}

/**
 * `canManage` is security.manage, and it governs ONLY the project-level parts:
 * the readiness banner and the button that turns authenticator sign-in on for
 * everyone. Everything below it - enrolling, showing the QR code, removing a
 * factor - is about the signed-in person's OWN account and always renders.
 *
 * That split is deliberate and should not be collapsed into a single gate. A
 * permission model that can stop somebody protecting their own login has a bug
 * in it, and locking a tech out of the whole card would do exactly that while
 * appearing to be a tightening.
 */
export function MfaCard({ orgId, canManage }: { orgId: string | null; canManage: boolean }) {
  const { current: currentOrg } = useOrg();
  const [factors, setFactors] = useState<MultiFactorInfo[]>([]);
  const [status, setStatus] = useState<ProjectStatus | null>(null);
  const [enroll, setEnroll] = useState<{ secret: TotpSecret; qr: string; key: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [requireMfa, setRequireMfa] = useState(false);

  const refresh = () => setFactors(enrolledFactors(auth.currentUser));

  useEffect(() => { refresh(); }, []);

  // myOrgs already resolved this, so the checkbox is correct on first paint
  // rather than flicking from off to on a moment later.
  useEffect(() => { setRequireMfa(currentOrg?.requireMfa === true); }, [currentOrg]);

  useEffect(() => {
    if (!orgId || !canManage) return;
    void (async () => {
      try {
        const r = await httpsCallable(functions, 'getMfaConfig')({ orgId });
        setStatus(r.data as ProjectStatus);
      } catch (err) {
        setStatus({ ok: false, ready: false, message: err instanceof Error ? err.message : 'Could not read the MFA setting.' });
      }
    })();
  }, [orgId, canManage]);

  async function run(key: string, fn: () => Promise<string>) {
    setBusy(key); setMsg(null);
    try { setMsg({ ok: true, text: await fn() }); }
    catch (err) { setMsg({ ok: false, text: mfaErrorText(err) }); }
    finally { setBusy(null); }
  }

  const start = () => run('start', async () => {
    const e = await beginEnrollment();
    setEnroll({ secret: e.secret, qr: await QRCode.toDataURL(e.qrUrl, { margin: 1, width: 220 }), key: e.manualKey });
    return 'Scan the code, then enter the six digits it shows.';
  });

  const confirm = () => run('confirm', async () => {
    if (!enroll) throw new Error('Start again.');
    await finishEnrollment(enroll.secret, code);
    setEnroll(null); setCode(''); refresh();
    return 'Two-factor authentication is on. You will be asked for a code at each sign-in.';
  });

  const verified = isVerified(auth.currentUser);
  const on = factors.length > 0;

  return (
    <section className="panel">
      <h2 className="panel-title">
        Two-factor authentication
        <span className={'tag ' + (on ? 'tag-ok' : 'tag-warn')} style={{ marginLeft: 10 }}>
          {on ? 'on' : 'off'}
        </span>
      </h2>
      <p className="panel-note">
        An authenticator app on your phone, asked for at each sign-in. Firebase refuses to issue a
        session until the code is right, so a stolen password alone is not enough.
      </p>

      {/* Project-level status, for whoever can act on it. */}
      {canManage && status && !status.ready && (
        <div className="banner banner-warn">
          {status.message}
          {status.detail && <div className="dim" style={{ marginTop: 4, fontSize: 11 }}>{status.detail}</div>}
        </div>
      )}
      {canManage && status?.ready && !status.enabled && (
        <div className="banner banner-warn">
          Identity Platform is ready, but authenticator-app sign-in is not switched on for this
          project yet.
          <button
            className="btn-link" style={{ marginLeft: 8 }} disabled={busy !== null}
            onClick={() => void run('enable', async () => {
              const r = await httpsCallable(functions, 'setTotpMfa')({ orgId, enable: true });
              setStatus({ ok: true, ready: true, ...(r.data as object) } as ProjectStatus);
              return 'Authenticator-app sign-in is now available for everyone in this project.';
            })}
          >
            {busy === 'enable' ? 'Turning on...' : 'Turn it on'}
          </button>
        </div>
      )}

      {/*
        * REQUIRE IT, for everybody in this org.
        *
        * Sits under the project-level banners on purpose: the order on screen
        * matches the order of the decisions. Authenticator sign-in has to be
        * available before it can be compulsory, and the server refuses the
        * second without the first rather than letting somebody build a lockout
        * and discover it at the next sign-in.
        */}
      {canManage && orgId && (
        <div className="mfa-require">
          <label className="mfa-require-row">
            <input
              type="checkbox"
              checked={requireMfa}
              disabled={busy !== null}
              onChange={(e) => {
                const want = e.target.checked;
                void run('require', async () => {
                  await httpsCallable(functions, 'setOrgSecurity')({ orgId, requireMfa: want });
                  setRequireMfa(want);
                  return want
                    ? 'Everyone in this organisation will be asked to set up an authenticator at their next sign-in.'
                    : 'Two-factor is no longer required. Anyone already enrolled stays enrolled.';
                });
              }}
            />
            <span>
              <strong>Require two-factor for everyone in this organisation</strong>
              <span className="dim" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
                Members without an authenticator are asked to set one up before the dashboard opens.
                Existing sessions are not ended.
              </span>
            </span>
          </label>
        </div>
      )}

      {msg && <div className={'banner ' + (msg.ok ? 'banner-ok' : 'banner-error')}>{msg.text}</div>}

      {!verified && (
        <div className="banner banner-warn">
          Firebase will not let you enrol until your email address is verified.
          <button
            className="btn-link" style={{ marginLeft: 8 }} disabled={busy !== null}
            onClick={() => void run('verify', async () => {
              await sendVerification();
              return 'Verification email sent. Open the link, then reload this page.';
            })}
          >
            Send the verification email
          </button>
        </div>
      )}

      {on && (
        <div className="mfa-factors">
          {factors.map((f) => (
            <div className="mfa-factor" key={f.uid}>
              <span>{f.displayName || 'Authenticator app'}</span>
              <button
                className="btn-link danger" disabled={busy !== null}
                onClick={() => {
                  if (!confirmRemove()) return;
                  void run('off', async () => {
                    await unenroll(f.uid);
                    refresh();
                    return 'Two-factor authentication is off.';
                  });
                }}
              >
                Turn off
              </button>
            </div>
          ))}
        </div>
      )}

      {!on && !enroll && (
        <button className="btn btn-primary" disabled={busy !== null || !verified} onClick={start}>
          {busy === 'start' ? 'Preparing...' : 'Set up two-factor'}
        </button>
      )}

      {enroll && (
        <div className="mfa-enroll">
          <img className="mfa-qr" src={enroll.qr} alt="Scan this with your authenticator app" />
          <div className="mfa-enroll-side">
            <div className="panel-note" style={{ marginTop: 0 }}>
              Scan with Google Authenticator, 1Password, Authy or similar. Cannot scan? Enter this
              key by hand:
            </div>
            <code className="mfa-key">{enroll.key}</code>
            <label className="field" style={{ marginTop: 10 }}>
              <span className="field-label">Six-digit code</span>
              <input
                className="field-input mono" inputMode="numeric" maxLength={6}
                placeholder="000000" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </label>
            <div className="row-form">
              <button className="btn btn-primary" disabled={busy !== null || code.length !== 6} onClick={confirm}>
                {busy === 'confirm' ? 'Checking...' : 'Confirm'}
              </button>
              <button className="btn" disabled={busy !== null} onClick={() => { setEnroll(null); setCode(''); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {on && (
        <p className="panel-note">
          There are no backup codes - that is a Firebase limitation, not a choice. If you lose the
          phone, an owner or admin has to reset your second factor from Team Members.
        </p>
      )}
    </section>
  );
}

/** Removing a second factor is a security downgrade, so it asks. */
function confirmRemove(): boolean {
  return confirm('Turn off two-factor authentication? Your account will be protected by its password alone.');
}
